import { DocumentViewer } from '@aecom-ai-engineering/react-components';
import { Checkbox, Spinner } from '@consigli/facade';
import { useSelectedCard, useServiceName } from '@consigli/hooks';
import { DocumentsType, ServiceName } from '@consigli/types';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/card';
import { createRenderers } from '@/features/document-viewer/renderers';
import { resolveDocumentFileType, resolveDocumentSrc } from '@/util/document-check';
import { BlobFilter } from '@/util/types';

import { DocumentCard } from './document-card';
import { DocumentCardSuggestion } from './document-card-suggestion';

interface DocumentListProps {
  pageRecords: DocumentsType[];
  setPageRecords: Dispatch<SetStateAction<DocumentsType[]>>;
  selectedTab: BlobFilter | undefined;
  searchText?: string;
  isFetching: boolean;
}

export const DocumentList = ({
  pageRecords,
  setPageRecords,
  isFetching,
  searchText,
  selectedTab,
}: DocumentListProps) => {
  const { t } = useTranslation();
  const { selectedCard, setSelectedCard } = useSelectedCard('');
  const serviceName = useServiceName();
  const showScheduleCheckFileType = serviceName === ServiceName.SCHEDULE_CHECK;
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);

  const activeDocumentIndex = pageRecords.findIndex((record) => record.id === activeDocumentId);
  const activeDocument = activeDocumentIndex >= 0 ? pageRecords[activeDocumentIndex] : null;

  const renderers = useMemo(() => createRenderers(), []);

  const getOnClickHandler = useCallback(
    (record: DocumentsType) => {
      return () => {
        setSelectedCard(record.id);
        setActiveDocumentId(record.id);
      };
    },
    [setSelectedCard],
  );
  const handleCheckboxClick = useCallback(
    (record: DocumentsType) => {
      setPageRecords((prevData: DocumentsType[]) =>
        prevData.map((item: DocumentsType) =>
          item.id === record.id ? { ...item, checked: !item.checked } : item,
        ),
      );
    },
    [setPageRecords],
  );
  return (
    <>
      <div className="overflow-auto h-[calc(100vh-25rem)] 2xl:h-[calc(100vh-29rem)]">
        {isFetching ? (
          <Spinner />
        ) : pageRecords.length === 0 ? (
          <div className="bg-day-light-3 w-full h-full flex flex-col items-center justify-center">
            <div className="text-day-neutral-subtle text-xl font-medium py-4">
              {t('document-list.no-documents-match-filter')}
            </div>
          </div>
        ) : (
          <>
            {pageRecords.map((record) => (
              <div className="flex items-center" key={record.id}>
                <div className="ml-1">
                  <Checkbox
                    value={record.id.toString()}
                    label=""
                    id={record.id.toString()}
                    checked={record.checked}
                    onChange={() => handleCheckboxClick(record)}
                  />
                </div>
                <div className="flex-1 ml-4">
                  <Card selected={selectedCard === record.id} onClick={getOnClickHandler(record)}>
                    {selectedTab === BlobFilter.SUGGESTION ? (
                      <DocumentCardSuggestion
                        document={record}
                        selected={selectedCard}
                        searchText={searchText}
                      />
                    ) : (
                      <DocumentCard
                        document={record}
                        selected={selectedCard}
                        searchText={searchText}
                        showScheduleCheckFileType={showScheduleCheckFileType}
                      />
                    )}
                  </Card>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
      {activeDocument && (
        <DocumentViewer
          open={activeDocumentIndex >= 0}
          onOpenChange={(details) => {
            if (!details.open) setActiveDocumentId(null);
          }}
          src={resolveDocumentSrc(activeDocument)}
          fileType={resolveDocumentFileType(activeDocument)}
          fileName={activeDocument.name}
          title={activeDocument.name}
          showSidebar={false}
          showNavigationActions={pageRecords.length > 1}
          previousDisabled={activeDocumentIndex <= 0}
          nextDisabled={activeDocumentIndex >= pageRecords.length - 1}
          onPrevious={() => setActiveDocumentId(pageRecords[activeDocumentIndex - 1]?.id ?? null)}
          onNext={() => setActiveDocumentId(pageRecords[activeDocumentIndex + 1]?.id ?? null)}
          renderers={renderers}
        />
      )}
    </>
  );
};
